<script setup>
import { ref, computed, defineProps, defineEmits, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { getCurrencyByCode, getCurrencyForLocale, DEFAULT_CURRENCY } from '../utils/currencyUtils';
import KanbanHeader from './KanbanHeader.vue';
import KanbanAPI from '../../../../api/kanban';

const props = defineProps({
  currentView: {
    type: String,
    default: 'dashboard',
  },
});

const emit = defineEmits(['switchView']);

const store = useStore();
const { locale } = useI18n();

// Funil selecionado, igual base usado em KanbanTab/ListTab
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Estatísticas agregadas vindas da API de funis (index)
const funnelStats = computed(() => selectedFunnel.value?.stats || null);

// Itens do Kanban carregados da API para o funil atual (como em ListTab)
const kanbanItems = ref([]);
const isLoadingItems = ref(false);

const fetchKanbanItems = async () => {
  if (!selectedFunnel.value?.id) {
    kanbanItems.value = [];
    return;
  }

  try {
    isLoadingItems.value = true;
    const response = await KanbanAPI.getItems(selectedFunnel.value.id);
    const items = response.data.items || response.data || [];
    kanbanItems.value = items;
  } catch (error) {
    console.error('[KanbanDashboard] Erro ao buscar itens do funil:', error);
    kanbanItems.value = [];
  } finally {
    isLoadingItems.value = false;
  }
};

onMounted(() => {
  if (selectedFunnel.value) {
    fetchKanbanItems();
  }
});

watch(
  selectedFunnel,
  (newFunnel, oldFunnel) => {
    if (newFunnel && newFunnel.id !== oldFunnel?.id) {
      fetchKanbanItems();
    }
  }
);

// Itens apenas do funil atual (defensivo, embora a API já filtre por funil)
const funnelItems = computed(() => {
  if (!selectedFunnel.value) return [];
  return (kanbanItems.value || []).filter(
    item => String(item.funnel_id) === String(selectedFunnel.value.id)
  );
});

// Colunas (estágios) derivadas do funil, como no KanbanTab
const columns = computed(() => {
  const funnel = selectedFunnel.value;
  if (!funnel?.stages) return [];

  const stagesArray = Object.entries(funnel.stages).map(([id, stage]) => ({
    id,
    title: stage.name,
    color: stage.color,
    position: parseInt(stage.position, 10) || 0,
  }));

  stagesArray.sort((a, b) => a.position - b.position);
  return stagesArray;
});

// Primeiro estágio do funil (usado para "leads novos")
const firstStageId = computed(() => {
  if (!selectedFunnel.value?.stages) return null;
  const stages = Object.entries(selectedFunnel.value.stages).map(
    ([id, stage]) => ({
      id,
      position: parseInt(stage.position, 10) || 0,
    })
  );
  stages.sort((a, b) => a.position - b.position);
  return stages[0]?.id || null;
});

// Métricas do cockpit
const totalLeads = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.total_items || 0;
  }
  return funnelItems.value.length;
});

const newLeads = computed(() => {
  if (!firstStageId.value) return 0;
  return funnelItems.value.filter(
    item => String(item.funnel_stage) === String(firstStageId.value)
  ).length;
});

const activeAgentsCount = computed(() => {
  const ids = new Set();
  funnelItems.value.forEach(item => {
    // Dados atuais: lista de agentes atribuídos em item.assigned_agents
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (agent?.id) {
          ids.add(agent.id);
        }
      });
    }
  });
  return ids.size;
});

const activeAgentsList = computed(() => {
  const map = new Map();
  funnelItems.value.forEach(item => {
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (agent?.id && !map.has(agent.id)) {
          map.set(agent.id, {
            id: agent.id,
            name: agent.name,
            email: agent.email,
            avatar_url: agent.avatar_url,
          });
        }
      });
    }
  });
  return Array.from(map.values()).slice(0, 5);
});

const topAgent = computed(() => {
  const counts = new Map();

  funnelItems.value.forEach(item => {
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (!agent?.id) return;
        const current = counts.get(agent.id) || {
          id: agent.id,
          name: agent.name,
          email: agent.email,
          avatar_url: agent.avatar_url,
          count: 0,
        };
        current.count += 1;
        counts.set(agent.id, current);
      });
    }
  });

  if (!counts.size) return null;

  return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0];
});

const totalValue = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.total_value || 0;
  }

  return funnelItems.value.reduce((sum, item) => {
    const raw = item.item_details?.value;
    const value = raw ? parseFloat(raw) : 0;
    if (Number.isNaN(value)) return sum;
    return sum + value;
  }, 0);
});

const leadsWithConversation = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_with_conversation || 0;
  }

  return funnelItems.value.filter(item => {
    return item.conversation || item.item_details?.conversation_id;
  }).length;
});

const leadsWithPendingChecklist = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_with_pending_checklist || 0;
  }

  return funnelItems.value.filter(item => {
    if (!Array.isArray(item.checklist)) return false;
    return item.checklist.some(check => !check.completed);
  }).length;
});

const unassignedLeads = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_without_agent || 0;
  }

  return funnelItems.value.filter(item => {
    return !Array.isArray(item.assigned_agents) || item.assigned_agents.length === 0;
  }).length;
});

const winRate = computed(() => {
  if (!funnelStats.value || !funnelStats.value.total_items) return 0;
  const won = funnelStats.value.won_items || 0;
  const total = funnelStats.value.total_items || 0;
  return Math.round((won / total) * 100);
});

const conversationsWithoutItem = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_conversations_without_item || 0;
});

const newConversationsWithoutItem24h = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_new_conversations_without_item_24h || 0;
});

const contactsWaitingForReply = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_contacts_waiting_for_reply || 0;
});

// Métricas de SLA perdido por etapa vindas da API de funis (index)
const slaBreachedByStage = computed(() => {
  if (!funnelStats.value) return {};
  return funnelStats.value.sla_breached_by_stage || {};
});

const nextChecklistAction = computed(() => {
  const candidates = [];

  funnelItems.value.forEach(item => {
    if (!Array.isArray(item.checklist)) return;

    item.checklist.forEach(check => {
      if (check.completed) return;
      if (!check.due_date) return;

      const due = new Date(check.due_date);
      if (Number.isNaN(due.getTime())) return;

      candidates.push({
        item,
        check,
        due,
      });
    });
  });

  if (!candidates.length) return null;

  candidates.sort((a, b) => a.due - b.due);
  return candidates[0];
});

const nextChecklistMeta = computed(() => {
  if (!nextChecklistAction.value || !selectedFunnel.value) return null;

  const { item } = nextChecklistAction.value;
  const stage = selectedFunnel.value.stages?.[item.funnel_stage];
  const stageName = stage?.name || item.funnel_stage;

  let agents = [];
  if (Array.isArray(item.assigned_agents)) {
    agents = item.assigned_agents
      .map(agent => agent?.name)
      .filter(Boolean);
  }

  const rawValue = item.item_details?.value;
  let formattedValue = null;
  if (rawValue) {
    const num = parseFloat(rawValue);
    if (!Number.isNaN(num)) {
      const rawCurrency = item.item_details.currency;
      const currency = rawCurrency?.code
        ? getCurrencyByCode(rawCurrency.code)
        : getCurrencyForLocale(locale.value);
      formattedValue = new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
      }).format(num);
    }
  }

  return {
    stageName,
    agents,
    formattedValue,
  };
});

// Etapas com itens em que o SLA configurado foi perdido (dados vindos do backend)
const slaBreachedStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  const stagesConfig = selectedFunnel.value.stages || {};
  const byStage = slaBreachedByStage.value || {};

  const result = Object.entries(byStage)
    .filter(([, count]) => Number(count) > 0)
    .map(([stageId, count]) => {
      const stage = stagesConfig[stageId] || {};
      return {
        id: stageId,
        name: stage.name || stageId,
        color: stage.color,
        count: Number(count),
      };
    });

  return result.sort((a, b) => b.count - a.count);
});

const topStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  const counts = {};
  funnelItems.value.forEach(item => {
    const stageId = item.funnel_stage;
    if (!stageId) return;
    counts[stageId] = (counts[stageId] || 0) + 1;
  });

  const result = Object.entries(counts).map(([id, count]) => {
    const stage = selectedFunnel.value.stages[id];
    return {
      id,
      name: stage?.name || `Etapa ${id}`,
      color: stage?.color,
      count,
    };
  });

  result.sort((a, b) => b.count - a.count);
  return result.slice(0, 3);
});

// Estado para integrar com o header
const searchResults = ref(null);
const activeFilters = ref(null);
const isDraggingActive = ref(false);
const activeDraggedItem = ref(null);

const handleFilter = filters => {
  activeFilters.value = filters || {};
};

const handleSearch = () => {
  // Mantém compatibilidade com o header; busca detalhada não é necessária no cockpit
};
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900">
    <KanbanHeader
      :current-view="currentView"
      :current-stage="null"
      :search-results="searchResults"
      :columns="columns"
      :active-filters="activeFilters"
      :kanban-items="funnelItems"
      :is-dragging-active="isDraggingActive"
      :dragged-item="activeDraggedItem"
      @filterApplied="handleFilter"
      @globalFilterChange="handleFilter"
      @search="handleSearch"
      @switchView="(...args) => emit('switchView', ...args)"
    />

    <div
      class="flex-1 overflow-y-auto bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 kanban-dashboard-scroll"
    >
      <div class="w-full px-4 py-4 md:py-6 space-y-6">
        <!-- Banner global de SLA perdido -->
        <div
          v-if="slaBreachedStages.length"
          class="rounded-lg px-4 py-3 flex items-center justify-between"
          :style="{
            backgroundColor: '#FEF2F2',
            border: '1px solid #DC2626',
          }"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="w-2 h-8 rounded-full"
              :style="{ backgroundColor: '#DC2626' }"
            />
            <div class="min-w-0">
              <div class="text-xs font-semibold uppercase tracking-wide" :style="{ color: '#7F1D1D' }">
                SLA de etapa perdido
              </div>
              <div class="text-sm font-medium truncate" :style="{ color: '#7F1D1D' }">
                {{ slaBreachedStages.reduce((sum, s) => sum + s.count, 0) }} lead(s) com SLA estourado em
                {{ slaBreachedStages.length }} etapa(s).
              </div>
            </div>
          </div>
        </div>

        <!-- Banner de destaque do agente com mais itens -->
        <div
          v-if="topAgent"
          class="rounded-lg px-4 py-3 flex items-center justify-between"
          :style="{
            backgroundColor: '#EEF2FF',
            border: '1px solid #6366F1',
          }"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-9 h-9 rounded-full border border-white bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 overflow-hidden"
            >
              <img
                v-if="topAgent.avatar_url"
                :src="topAgent.avatar_url"
                :alt="topAgent.name"
                class="w-full h-full object-cover"
              />
              <span v-else>
                {{ topAgent.name ? topAgent.name.charAt(0).toUpperCase() : '?' }}
              </span>
            </div>
            <div class="min-w-0">
              <div class="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Agente com mais itens
              </div>
              <div class="text-sm font-medium text-slate-900 truncate">
                {{ topAgent.name || 'Agente sem nome' }}
              </div>
              <div class="text-[11px] text-slate-600 truncate">
                {{ topAgent.email }} · {{ topAgent.count }} item(ns) atribuídos
              </div>
            </div>
          </div>
        </div>

        <!-- Banner de próxima ação sugerida -->
        <div
          v-if="nextChecklistAction"
          class="rounded-lg px-4 py-3 flex items-start gap-3"
          :style="{
            backgroundColor: '#FFFBEB',
            border: '1px solid #FDE68A',
          }"
        >
          <div
            class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            :style="{
              backgroundColor: '#FACC15',
            }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1F2937"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <div class="text-xs font-semibold uppercase tracking-wide" :style="{ color: '#92400E' }">
                Próxima ação sugerida
              </div>
              <div
                class="px-2 py-0.5 rounded-full text-[10px] font-medium"
                :style="{
                  backgroundColor: '#FEF3C7',
                  color: '#92400E',
                }"
              >
                Checklist
              </div>
            </div>
            <div class="mt-1 text-sm font-medium truncate" :style="{ color: '#78350F' }">
              {{ nextChecklistAction.item.item_details?.title }}
            </div>
            <div class="mt-0.5 text-xs truncate" :style="{ color: '#92400E' }">
              {{ nextChecklistAction.check.text }}
            </div>
            <div class="mt-0.5 text-[11px] font-medium" :style="{ color: '#B45309' }">
              {{ $t('KANBAN.DEADLINE') }}:
              {{
                new Date(nextChecklistAction.due).toLocaleString(locale || 'en', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              }}
            </div>
            <div
              v-if="nextChecklistMeta"
              class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]"
              :style="{ color: '#92400E' }"
            >
              <span
                class="px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-medium"
              >
                ID #{{ nextChecklistAction.item.id }}
              </span>
              <span
                v-if="nextChecklistMeta.stageName"
                class="px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-medium truncate"
              >
                Etapa: {{ nextChecklistMeta.stageName }}
              </span>
              <span
                v-if="nextChecklistMeta.agents && nextChecklistMeta.agents.length"
                class="px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-medium truncate"
              >
                Agente(s): {{ nextChecklistMeta.agents.join(', ') }}
              </span>
              <span
                v-if="nextChecklistMeta.formattedValue"
                class="px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-medium"
              >
                {{ $t('KANBAN.DASHBOARD.CHECKLIST_VALUE') }} {{ nextChecklistMeta.formattedValue }}
              </span>
            </div>
          </div>
        </div>

        <!-- Linha principal de cards -->
        <div class="mb-1">
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {{ $t('KANBAN.DASHBOARD.FUNNEL_OVERVIEW') }}
          </h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {{ $t('KANBAN.DASHBOARD.FUNNEL_LEADS') }}
            </div>
            <div class="mt-2 text-2xl font-semibold">
              {{ totalLeads }}
            </div>
          </div>

          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {{ $t('KANBAN.DASHBOARD.NEW_LEADS') }}
            </div>
            <div class="mt-2 text-2xl font-semibold text-emerald-500">
              {{ newLeads }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('KANBAN.DASHBOARD.LEADS_FIRST_STAGE') }}
            </p>
          </div>

          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Agentes ativos
            </div>
            <div class="mt-2 flex items-center justify-between gap-3">
              <div class="text-2xl font-semibold text-blue-500">
                {{ activeAgentsCount }}
              </div>
              <div class="flex -space-x-2">
                <div
                  v-for="agent in activeAgentsList"
                  :key="agent.id"
                  class="w-7 h-7 rounded-full border border-white dark:border-slate-900 bg-slate-200 text-[11px] font-medium text-slate-700 flex items-center justify-center overflow-hidden"
                >
                  <img
                    v-if="agent.avatar_url"
                    :src="agent.avatar_url"
                    :alt="agent.name"
                    class="w-full h-full object-cover"
                  />
                  <span v-else>
                    {{ agent.name ? agent.name.charAt(0).toUpperCase() : '?' }}
                  </span>
                </div>
              </div>
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Com pelo menos um lead no funil
            </p>
          </div>

          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Taxa de ganho
            </div>
            <div class="mt-2 text-2xl font-semibold text-emerald-600">
              {{ winRate }}%
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Percentual de itens marcados como ganhos no funil
            </p>
          </div>
        </div>

        <!-- Banner de etapas com SLA perdido -->
        <div v-if="slaBreachedStages.length" class="space-y-2">
          <div
            v-for="stage in slaBreachedStages"
            :key="stage.id"
            class="flex items-center justify-between rounded-md px-3 py-2"
            :style="{
              backgroundColor: '#FFFFFF',
              border: '1px solid #DC2626',
            }"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: stage.color || '#F97316' }"
              />
              <div class="text-xs min-w-0">
                <div class="font-medium truncate" :style="{ color: '#000000' }">
                  Etapa {{ stage.name }} com SLA perdido
                </div>
                <div class="text-[11px]" :style="{ color: '#000000' }">
                  {{ stage.count }} lead(s) com SLA de etapa estourado
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Linha secundária de cards -->
        <div class="mt-6 mb-1">
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Detalhes dos leads
          </h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {{ $t('KANBAN.DASHBOARD.TOTAL_OPEN_VALUE') }}
            </div>
            <div class="mt-2 text-xl font-semibold">
              {{
                new Intl.NumberFormat(getCurrencyForLocale(locale.value).locale, {
                  style: 'currency',
                  currency: getCurrencyForLocale(locale.value).code,
                }).format(totalValue)
              }}
            </div>
          </div>

          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {{ $t('KANBAN.DASHBOARD.LEADS_WITH_CONVERSATION') }}
            </div>
            <div class="mt-2 text-2xl font-semibold text-violet-500">
              {{ leadsWithConversation }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('KANBAN.DASHBOARD.ITEMS_LINKED_CONVERSATIONS') }}
            </p>
          </div>

          <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 px-4 py-3">
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {{ $t('KANBAN.DASHBOARD.LEADS_PENDING_TASKS') }}
            </div>
            <div class="mt-2 text-2xl font-semibold text-amber-500">
              {{ leadsWithPendingChecklist }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('KANBAN.DASHBOARD.ITEMS_INCOMPLETE_CHECKLIST') }}
            </p>
          </div>
        </div>

        <!-- Resumo rápido de itens sem agente -->
        <div
          v-if="unassignedLeads"
          class="rounded-lg px-4 py-3 flex items-center justify-between"
          :style="{
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
          }"
        >
          <div class="text-sm font-medium" style="color: #7F1D1D;">
            {{ $t('KANBAN.DASHBOARD.UNASSIGNED_LEADS', { count: unassignedLeads }) }}
          </div>
        </div>

        <!-- Conversas e contatos para ação rápida -->
        <div
          v-if="conversationsWithoutItem || contactsWaitingForReply || newConversationsWithoutItem24h"
          class="mt-6 mb-1"
        >
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Conversas e contatos para ação rápida
          </h3>
        </div>
        <div
          v-if="conversationsWithoutItem || contactsWaitingForReply || newConversationsWithoutItem24h"
          class="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          <div
            v-if="contactsWaitingForReply"
            class="rounded-xl shadow-sm px-4 py-3"
            :style="{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
            }"
          >
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Contatos aguardando resposta
            </div>
            <div class="mt-2 text-2xl font-semibold text-red-500">
              {{ contactsWaitingForReply }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Conversas sem primeira resposta ou aguardando nova interação.
            </p>
          </div>

          <div
            v-if="conversationsWithoutItem"
            class="rounded-xl shadow-sm px-4 py-3"
            :style="{
              backgroundColor: '#EFF6FF',
              border: '1px solid #BFDBFE',
            }"
          >
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Conversas sem item no funil
            </div>
            <div class="mt-2 text-2xl font-semibold text-slate-800 dark:text-slate-100">
              {{ conversationsWithoutItem }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Crie itens para acompanhar essas oportunidades no Kanban.
            </p>
          </div>

          <div
            v-if="newConversationsWithoutItem24h"
            class="rounded-xl shadow-sm px-4 py-3"
            :style="{
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A',
            }"
          >
            <div class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Novas conversas sem item (24h)
            </div>
            <div class="mt-2 text-2xl font-semibold text-amber-600">
              {{ newConversationsWithoutItem24h }}
            </div>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Priorize estruturar essas conversas em itens do funil.
            </p>
          </div>
        </div>

        <!-- Etapas com mais leads -->
        <div class="rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Etapas com mais leads
            </h3>
          </div>

          <div v-if="topStages.length" class="space-y-2">
            <div
              v-for="stage in topStages"
              :key="stage.id"
              class="flex items-center justify-between gap-3"
            >
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="w-2 h-2 rounded-full"
                  :style="{ backgroundColor: stage.color || '#64748B' }"
                />
                <span class="text-sm text-slate-700 dark:text-slate-200 truncate">
                  {{ stage.name }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-24 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div
                    class="h-full rounded-full bg-woot-500"
                    :style="{ width: Math.min(100, (stage.count / (topStages[0]?.count || 1)) * 100) + '%' }"
                  />
                </div>
                <span class="text-xs font-medium text-slate-600 dark:text-slate-300 w-8 text-right">
                  {{ stage.count }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-xs text-slate-500 dark:text-slate-400 italic"
          >
            Nenhum lead encontrado neste funil.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-dashboard-scroll {
  scrollbar-width: none; /* Firefox */
}

.kanban-dashboard-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}
</style>
